import * as React from 'react';
import { memo } from 'react';
import * as Colors from '../../consts/variables';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { MarketContext } from '../../context/markets';
import { IBreadcrumb } from '../../interfaces';
import { Link } from './../Link';
import parse from 'html-react-parser';
import { HomeBreadcrumbIcon } from '../Icons/HomeBreadcrumbIcon';

const styles: Record<string, React.CSSProperties> = {
    wrapper: {
        margin: '0 auto',
        color: Colors.softBlue,
    },
    withLink: {
        display: 'flex',
        flexDirection: 'row',
        marginBottom: 0,
        fontWeight: 600,
    },
    slash: {
        color: Colors.breadcrumbActiveColor,
        margin: '0 8px',
    },
    icon: {
        position: 'relative',
        top: '2px',
    },
    withoutLink: {
        display: 'flex',
        flexDirection: 'row',
        color: Colors.breadcrumbActiveColor,
        marginBottom: 0,
    },
    lastItemText: {
        maxWidth: '40ch',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
        margin: '0',
    },
};

const BreadcrumbHomeIcon = () => (
    <span style={styles.icon}>
        <HomeBreadcrumbIcon width={14} height={14} />
    </span>
);

interface PropsSelf {
    breadcrumbs: IBreadcrumb[];
    isSeparatorShown?: boolean;
    className?: string;
}

type IProps = PropsSelf;

const getFullUrl = (url: string, portalId: string): string =>
    url && url.indexOf('http') === -1 ? `https://www.${portalId}${url}` : url;

const Breadcrumbs = memo(({ breadcrumbs, isSeparatorShown = true, className }: IProps) => {
    const { pageData } = useTypedSelector((state) => state);
    const { markets } = React.useContext(MarketContext);
    const { isCruise1st } = markets;
    const { portalId } = pageData?.brandConfig;

    const last = breadcrumbs.length - 1;
    const links = breadcrumbs
        .filter((item) => Boolean(item))
        .map(({ url, text }, index) => {
            const isHome = index === 0 && url === '/';
            const isLast = index === last;
            return url && url.length > 0 && !isLast ? (
                <li style={styles.withLink} key={`breadcrumbs:${index}`}>
                    {index > 0 && <div style={styles.slash}>/</div>}
                    <Link href={url} aria-label={isHome ? text : undefined}>
                        {isHome ? <BreadcrumbHomeIcon /> : parse(text)}
                    </Link>
                </li>
            ) : (
                <li style={styles.withoutLink} key={`breadcrumbs:${index}`}>
                    {index > 0 && <div style={styles.slash}>/</div>}
                    {isHome ? (
                        <span style={styles.slash} aria-label={text}>
                            <BreadcrumbHomeIcon />
                        </span>
                    ) : (
                        <div
                            style={
                                isLast ? { ...styles.slash, ...styles.lastItemText } : styles.slash
                            }
                        >
                            {parse(text)}
                        </div>
                    )}
                </li>
            );
        });
    const breadcrumbsData = {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs
            .filter((item) => Boolean(item))
            .map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                item: {
                    '@id': getFullUrl(item.url, portalId),
                    name: item.text,
                },
            })),
    };
    const cruise1stClassName = isCruise1st ? 'darkBlue' : '';

    const wrapperStyle = isSeparatorShown
        ? { ...styles.wrapper, ...styles.separator }
        : styles.wrapper;
    return (
        <>
            <ol className={`breadcrumbs ${cruise1stClassName} ${className}`} style={wrapperStyle}>
                {links}
            </ol>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbsData) }}
            />
        </>
    );
});
export { Breadcrumbs };
